import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    createCollegeContact,
    fetchCollegeContacts,
    deleteCollegeContact,
    updateCollegeContact
} from '../services/collegeContactsService';
import EditIcon from '../images/buttons/Edit.svg';
import DeleteIcon from '../images/buttons/Delete.svg';
import type { CollegeContactListItem, CollegeContactResponse } from '../types/collegeContacts';
import { isValidCollegeContactEmail, isValidUsCollegeContactPhone } from '../utils/contactFieldValidation';
import '../styles/college-contacts-panel.css';

type DraftContact = {
    fullName: string;
    email: string;
    phone: string;
    title: string;
};

type FieldErrors = {
    fullName?: string;
    email?: string;
    phone?: string;
};

const emptyDraftContact = (): DraftContact => ({
    fullName: '',
    email: '',
    phone: '',
    title: ''
});

function mapResponseToListItem(c: CollegeContactResponse): CollegeContactListItem {
    return {
        id: c.id,
        fullName: c.fullName,
        email: c.email,
        phone: c.phone,
        title: c.title,
        createdAt: c.createdAt
    };
}

interface CollegeContactsPanelProps {
    isOpen: boolean;
    onClose: () => void;
    collegeName: string;
    collegeId: number | null;
    athleteId: number | null;
    contacts: CollegeContactListItem[];
    onContactsLoaded: (collegeId: number, contacts: CollegeContactListItem[]) => void;
    onDeleteContact: (contactId: number) => void;
    /** Guardians and other read-only viewers can load contacts but not create/delete. */
    readOnly?: boolean;
}

const CollegeContactsPanel: React.FC<CollegeContactsPanelProps> = ({
    isOpen,
    onClose,
    collegeName,
    collegeId,
    athleteId,
    contacts,
    onContactsLoaded,
    onDeleteContact,
    readOnly = false,
}) => {
    const [draft, setDraft] = useState<DraftContact>(emptyDraftContact());
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [saveError, setSaveError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingContacts, setIsLoadingContacts] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [deletingContactId, setDeletingContactId] = useState<number | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [editingContactId, setEditingContactId] = useState<number | null>(null);

    const friendlyError = (err: unknown, fallback: string) => {
        const message = err instanceof Error ? err.message : fallback;
        if (/exception|sql|stack trace/i.test(message)) {
            return fallback;
        }
        return message || fallback;
    };

    useEffect(() => {
        if (isOpen && collegeId != null) {
            setDraft(emptyDraftContact());
            setFieldErrors({});
            setSaveError(null);
            setEditingContactId(null);
        }
    }, [isOpen, collegeId]);

    useEffect(() => {
        const load = async () => {
            if (!isOpen || !athleteId || !collegeId) {
                return;
            }
            if (contacts.length > 0) {
                return;
            }

            setIsLoadingContacts(true);
            setFetchError(null);

            try {
                const fetched = await fetchCollegeContacts(athleteId, collegeId);
                const rows = fetched.map(mapResponseToListItem);
                onContactsLoaded(collegeId, rows);
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to load contacts';
                setFetchError(errorMessage);
            } finally {
                setIsLoadingContacts(false);
            }
        };

        void load();
    }, [isOpen, athleteId, collegeId, contacts.length, onContactsLoaded]);

    const updateDraft = useCallback((patch: Partial<DraftContact>) => {
        setDraft((prev) => ({ ...prev, ...patch }));
        setFieldErrors({});
    }, []);

    const handleAddContact = async () => {
        if (readOnly) {
            return;
        }
        if (!athleteId || !collegeId) {
            setSaveError('Missing athlete or college information');
            return;
        }

        setSaveError(null);

        const name = draft.fullName.trim();
        const email = draft.email.trim();
        const phone = draft.phone.trim();
        const title = draft.title.trim();

        const nextErrors: FieldErrors = {};

        if (!name) {
            nextErrors.fullName = 'Name is required';
        }
        if (email && !isValidCollegeContactEmail(email)) {
            nextErrors.email = 'Enter a valid email address';
        }
        if (phone && !isValidUsCollegeContactPhone(phone)) {
            nextErrors.phone = 'Enter a valid US phone number (10 digits)';
        }

        if (Object.keys(nextErrors).length > 0) {
            setFieldErrors(nextErrors);
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                fullName: name,
                email: email || null,
                phone: phone || null,
                title: title || null
            };
            if (editingContactId != null) {
                await updateCollegeContact(athleteId, editingContactId, payload);
            } else {
                await createCollegeContact(athleteId, collegeId, payload);
            }

            const refreshed = await fetchCollegeContacts(athleteId, collegeId);
            onContactsLoaded(collegeId, refreshed.map(mapResponseToListItem));
            setDraft(emptyDraftContact());
            setFieldErrors({});
            setEditingContactId(null);
        } catch (err) {
            setSaveError(friendlyError(err, editingContactId != null ? 'Could not save that contact.' : 'Failed to save contact'));
        } finally {
            setIsSaving(false);
        }
    };

    const startEdit = (contact: CollegeContactListItem) => {
        setEditingContactId(contact.id);
        setDraft({
            fullName: contact.fullName,
            email: contact.email ?? '',
            phone: contact.phone ?? '',
            title: contact.title ?? ''
        });
        setFieldErrors({});
        setSaveError(null);
    };

    const cancelEdit = () => {
        setEditingContactId(null);
        setDraft(emptyDraftContact());
        setFieldErrors({});
        setSaveError(null);
    };

    const handleDelete = async (contactId: number) => {
        if (readOnly) {
            return;
        }
        if (!athleteId) {
            setDeleteError('Missing athlete information');
            return;
        }

        setDeletingContactId(contactId);
        setDeleteError(null);

        try {
            await deleteCollegeContact(athleteId, contactId);
            onDeleteContact(contactId);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete contact';
            setDeleteError(errorMessage);
        } finally {
            setDeletingContactId(null);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <>
            <div className="contacts-panel-overlay" data-testid="contacts-panel-overlay" />
            <div className="contacts-panel" data-testid="contacts-panel">
                <div className="contacts-panel-header">
                    <button
                        type="button"
                        className="contacts-panel-close"
                        onClick={onClose}
                        aria-label="Close panel"
                        data-testid="contacts-panel-close"
                    >
                        ✕
                    </button>
                    <h2 className="contacts-panel-title">My contacts</h2>
                    <p className="contacts-panel-subtitle">{collegeName}</p>
                    <p className="contacts-panel-description">
                        {readOnly ? "Contacts saved for outreach" : "Save contacts here for easier outreach"}
                    </p>
                </div>

                <div className="contacts-panel-content">
                    {saveError && (
                        <div className="contacts-error-message" data-testid="contacts-save-error">
                            {saveError}
                        </div>
                    )}

                    {!readOnly && (
                    <div className="contacts-form-section">
                        <div className="contacts-draft-block">
                            <label className="contacts-field">
                                <span className="contacts-field-label">Full name</span>
                                <input
                                    type="text"
                                    className={`contacts-input${fieldErrors.fullName ? ' contacts-input--error' : ''}`}
                                    placeholder="First and last name"
                                    value={draft.fullName}
                                    onChange={(e) => updateDraft({ fullName: e.target.value })}
                                    autoComplete="name"
                                />
                                {fieldErrors.fullName && (
                                    <span className="contacts-field-error">{fieldErrors.fullName}</span>
                                )}
                            </label>
                            <label className="contacts-field">
                                <span className="contacts-field-label">Email</span>
                                <input
                                    type="email"
                                    className={`contacts-input${fieldErrors.email ? ' contacts-input--error' : ''}`}
                                    placeholder="Email"
                                    value={draft.email}
                                    onChange={(e) => updateDraft({ email: e.target.value })}
                                    autoComplete="email"
                                />
                                {fieldErrors.email && (
                                    <span className="contacts-field-error">{fieldErrors.email}</span>
                                )}
                            </label>
                            <label className="contacts-field">
                                <span className="contacts-field-label">Phone</span>
                                <input
                                    type="tel"
                                    className={`contacts-input${fieldErrors.phone ? ' contacts-input--error' : ''}`}
                                    placeholder="Phone"
                                    value={draft.phone}
                                    onChange={(e) => updateDraft({ phone: e.target.value })}
                                    autoComplete="tel"
                                />
                                {fieldErrors.phone && (
                                    <span className="contacts-field-error">{fieldErrors.phone}</span>
                                )}
                            </label>
                            <label className="contacts-field">
                                <span className="contacts-field-label">Title</span>
                                <input
                                    type="text"
                                    className="contacts-input"
                                    placeholder="Title"
                                    value={draft.title}
                                    onChange={(e) => updateDraft({ title: e.target.value })}
                                />
                            </label>
                        </div>

                        <div className="contacts-form-actions">
                            <button
                                type="button"
                                className="contacts-pill-btn"
                                onClick={() => void handleAddContact()}
                                disabled={isSaving}
                                data-testid={editingContactId != null ? 'contacts-save-contact' : 'contacts-add-contact'}
                            >
                                {isSaving ? 'Saving…' : editingContactId != null ? 'Save changes' : '+ Contact'}
                            </button>
                            {editingContactId != null && (
                                <button
                                    type="button"
                                    className="contacts-cancel-btn"
                                    onClick={cancelEdit}
                                    data-testid="contacts-cancel-edit"
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                    </div>
                    )}

                    <div className="contacts-list-section">
                        <h3 className="contacts-list-heading">Contact list</h3>
                        {deleteError && (
                            <div className="contacts-error-message" data-testid="contacts-delete-error">
                                {deleteError}
                            </div>
                        )}
                        {isLoadingContacts ? (
                            <div className="contacts-loading" data-testid="contacts-loading">
                                Loading contacts...
                            </div>
                        ) : fetchError ? (
                            <div className="contacts-fetch-error" data-testid="contacts-fetch-error">
                                {fetchError}
                            </div>
                        ) : (
                            <div className="contacts-list" data-testid="contacts-list">
                                {contacts.length === 0 ? (
                                    <p className="contacts-empty-message">No saved contacts yet.</p>
                                ) : (
                                    contacts.map((c) => (
                                        <div key={c.id} className="contacts-list-item" data-testid={`contact-row-${c.id}`}>
                                            <div className="contacts-list-item-main">
                                                <div className="contacts-list-name">{c.fullName}</div>
                                                {c.title ? (
                                                    <div className="contacts-list-meta">
                                                        Title: {c.title}
                                                    </div>
                                                ) : null}
                                                {c.email ? (
                                                    <div className="contacts-list-meta">
                                                        Email: {c.email}
                                                    </div>
                                                ) : null}
                                                {c.phone ? (
                                                    <div className="contacts-list-meta">
                                                        Phone: {c.phone}
                                                    </div>
                                                ) : null}
                                            </div>
                                            {!readOnly && (
                                            <div className="contacts-list-actions">
                                                <button
                                                    type="button"
                                                    className="contacts-list-icon-btn"
                                                    onClick={() => startEdit(c)}
                                                    aria-label="Edit contact"
                                                    title="Edit contact"
                                                    data-testid={`edit-contact-${c.id}`}
                                                >
                                                    <img src={EditIcon} alt="" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="contacts-list-icon-btn"
                                                    onClick={() => void handleDelete(c.id)}
                                                    disabled={deletingContactId === c.id}
                                                    aria-label="Delete contact"
                                                    title="Delete contact"
                                                    data-testid={`delete-contact-${c.id}`}
                                                >
                                                    <img src={DeleteIcon} alt="" />
                                                </button>
                                            </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default CollegeContactsPanel;
